import React, {useEffect, useRef, useState} from 'react';
import {Activity} from 'lucide-react';

import {Tracker} from "./Tracker";

const exercises = {
    squat: {
        name: "Squat",
        description: "Lower your body by bending knees to 90 degrees",
        targetAngles: { knee: [80, 100] },
        readyAngle: 160,
        jointType: 'knee',
        instructions: [
            "Stand with feet shoulder-width apart",
            "Keep your back straight throughout",
            "Bend knees to 90 degrees",
            "Push through heels to stand up"
        ],
        feedback: {
            ready: "Stand up straight. Ready to begin",
            starting: "Good! Start bending your knees",
            tooHigh: "Go lower. Bend your knees more",
            almostThere: "Almost there! A bit more",
            perfect: "Perfect! Hold this position",
            tooLow: "Come up a little bit",
            rising: "Good! Now push back up"
        }
    },
    armRaise: {
        name: "Shoulder Flexion",
        description: "Raise arms forward to shoulder height",
        targetAngles: { shoulder: [80, 100] },
        readyAngle: 160,
        jointType: 'shoulder',
        instructions: [
            "Stand with arms at your sides",
            "Keep arms straight",
            "Raise arms forward slowly",
            "Stop at shoulder height"
        ],
        feedback: {
            ready: "Arms down. Ready to start",
            starting: "Good! Start raising your arms",
            tooHigh: "Lower your arms slightly",
            almostThere: "Almost at shoulder height",
            perfect: "Perfect height! Hold it",
            tooLow: "Raise your arms higher"
        }
    },
    lateralRaise: {
        name: "Lateral Arm Raise",
        description: "Raise arms out to the sides",
        targetAngles: { shoulder: [80, 100] },
        readyAngle: 160,
        jointType: 'shoulder',
        instructions: [
            "Stand with arms at sides",
            "Raise arms out to sides",
            "Keep arms straight",
            "Reach shoulder height"
        ],
        feedback: {
            ready: "Arms at sides. Get ready",
            starting: "Good! Raise arms to sides",
            tooHigh: "Lower your arms a bit",
            almostThere: "Almost there! Keep going",
            perfect: "Perfect! Hold this position",
            tooLow: "Raise your arms higher"
        }
    },
    kneeRaise: {
        name: "Knee Raise",
        description: "Lift knee towards chest",
        targetAngles: { hip: [80, 100] },
        readyAngle: 160,
        jointType: 'hip',
        instructions: [
            "Stand on one leg",
            "Lift opposite knee up",
            "Raise to hip level",
            "Lower slowly"
        ],
        feedback: {
            ready: "Stand on one leg. Ready",
            starting: "Good! Lift your knee up",
            tooHigh: "Lower your knee slightly",
            almostThere: "Almost to hip level",
            perfect: "Perfect! Hold it there",
            tooLow: "Lift your knee higher"
        }
    }
};
const useMediaPipePose = (videoRef, canvasRef, isActive, onPoseDetected) => {
    const poseRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    const animationFrameRef = useRef(null);
    const processingRef = useRef(false);
    const lastFrameTimeRef = useRef(0);

    useEffect(() => {
        if (!isActive || !videoRef.current || !canvasRef.current) return;

        let isMounted = true;


        const initializePose = async () => {
            try {
                // Wait for MediaPipe to load with retry logic
                let retries = 0;
                while (typeof window.Pose === 'undefined' && retries < 20) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                    retries++;
                }

                if (typeof window.Pose === 'undefined') {
                    setError('MediaPipe libraries not loaded. Please refresh the page.');
                    return;
                }

                const pose = new window.Pose({
                    locateFile: (file) => {
                        console.log(`Loading MediaPipe file: ${file}`);
                        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
                    }
                });

                pose.setOptions({
                    modelComplexity: 1,
                    smoothLandmarks: true,
                    enableSegmentation: true,
                    minDetectionConfidence: 0.8,
                    minTrackingConfidence: 0.5
                });

                pose.onResults((results) => {
                    if (!isMounted) return;

                    const canvas = canvasRef.current;
                    if (!canvas) return;

                    const ctx = canvas.getContext('2d');
                    //ctx.save();


                    //ctx.clearRect(0, 0, canvas.width, canvas.height);

                    if (results.segmentationMask){
                        console.log(results.segmentationMask);
                    }
                    if (results.poseLandmarks) {
                        // Enhanced skeleton drawing with color-coded joints
                        const connections = window.POSE_CONNECTIONS;

                        // Draw connections with gradient effect
                        connections.forEach(([start, end]) => {
                            const startPoint = results.poseLandmarks[start];
                            const endPoint = results.poseLandmarks[end];

                            ctx.beginPath();
                            ctx.moveTo(startPoint.x * canvas.width, startPoint.y * canvas.height);
                            ctx.lineTo(endPoint.x * canvas.width, endPoint.y * canvas.height);

                            // Color code based on body part
                            let color = '#00FF00'; // Default green
                            if (start >= 11 && start <= 16 || end >= 11 && end <= 16) {
                                color = '#3B82F6'; // Blue for arms
                            } else if (start >= 23 && start <= 28 || end >= 23 && end <= 28) {
                                color = '#F59E0B'; // Orange for legs
                            } else if (start >= 0 && start <= 10 || end >= 0 && end <= 10) {
                                color = '#10B981'; // Emerald for torso/head
                            }

                            ctx.strokeStyle = color;
                            ctx.lineWidth = 5;
                            ctx.lineCap = 'round';
                            ctx.stroke();
                        });

                        // Draw landmarks with different sizes for key joints
                        results.poseLandmarks.forEach((landmark, index) => {
                            const x = landmark.x * canvas.width;
                            const y = landmark.y * canvas.height;

                            // Key joints (shoulders, elbows, hips, knees, ankles)
                            const keyJoints = [11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
                            const isKeyJoint = keyJoints.includes(index);

                            ctx.beginPath();
                            ctx.arc(x, y, isKeyJoint ? 8 : 4, 0, 2 * Math.PI);

                            // Color code joints
                            if (index >= 11 && index <= 16) {
                                ctx.fillStyle = isKeyJoint ? '#3B82F6' : '#60A5FA';
                            } else if (index >= 23 && index <= 28) {
                                ctx.fillStyle = isKeyJoint ? '#F59E0B' : '#FCD34D';
                            } else {
                                ctx.fillStyle = isKeyJoint ? '#10B981' : '#34D399';
                            }

                            ctx.fill();

                            // Add white border for visibility
                            ctx.strokeStyle = 'white';
                            ctx.lineWidth = 2;
                            ctx.stroke();
                        });

                        const angles = extractAngles(results.poseLandmarks);
                        if (angles && onPoseDetected) {
                            onPoseDetected(angles);
                        }
                    }

                    ctx.restore();
                    processingRef.current = false;
                });

                poseRef.current = pose;

                // Manual frame processing with throttling
                const processFrame = async () => {
                    if (!isMounted || !videoRef.current || !poseRef.current) return;

                    const now = Date.now();
                    const timeSinceLastFrame = now - lastFrameTimeRef.current;

                    // Process at ~30 FPS (every 33ms) to prevent overload
                    if (timeSinceLastFrame >= 33) {
                        try {
                            if (videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA && !processingRef.current) {
                                processingRef.current = true;
                                lastFrameTimeRef.current = now;
                                await poseRef.current.send({ image: videoRef.current });
                            }
                        } catch (err) {
                            console.error('Frame processing error:', err);
                            processingRef.current = false;
                        }
                    }

                    animationFrameRef.current = requestAnimationFrame(processFrame);
                };

                // Start processing frames
                setIsReady(true);
                processFrame();

            } catch (err) {
                console.error('MediaPipe error:', err);
                if (isMounted) {
                    setError('Failed to initialize pose tracking. Please refresh.');
                }
            }
        };

        initializePose();

        return () => {
            isMounted = false;
            processingRef.current = false;
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (poseRef.current) {
                try {
                    poseRef.current.close();
                } catch (err) {
                    console.error('Error closing pose:', err);
                }
            }
        };
    }, [isActive, videoRef, canvasRef, onPoseDetected]);

    return { isReady, error };
};

export default function Pose() {
    const [view, setView] = useState('exercise');
    const [selectedExercise, setSelectedExercise] = useState(exercises.squat);
    const [sessions, setSessions] = useState([]);
    const [voiceEnabled, setVoiceEnabled] = useState(true);

    // Load MediaPipe scripts
    useEffect(() => {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = src;
                script.crossOrigin = 'anonymous';
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
            });
        };

        const loadMediaPipe = async () => {
            try {
                await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js');
                await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/pose/pose.js');
                console.log('MediaPipe loaded successfully');
            } catch (error) {
                console.error('Failed to load MediaPipe:', error);
            }
        };

        loadMediaPipe();
    }, []);

    const handleExerciseComplete = (sessionData) => {
        setSessions(prev => [...prev, { ...sessionData, id: Date.now() }]);
        setView('dashboard');
        setSelectedExercise(null);
    };

    const startExercise = (exerciseKey) => {
        setSelectedExercise(exercises[exerciseKey]);
        setView('exercise');
    };

    return (
        <div className="app">


            <main className="main-content">
                {view === 'home' && (
                    <div className="home">fff
                        <div className="hero">
                            <h1>Virtual Physical Therapy 🏥</h1>
                            <p>Real-time coaching with voice guidance</p>
                        </div>

                        <div className="exercise-grid">
                            {Object.keys(exercises).map(key => (
                                <div key={key} className="exercise-card" onClick={() => startExercise(key)}>
                                    <div className="exercise-icon">
                                        <Activity size={40} />
                                    </div>
                                    <h3>{exercises[key].name}</h3>
                                    <p>{exercises[key].description}</p>
                                    <button className="btn-card">Start →</button>
                                </div>
                            ))}
                        </div>

                        {sessions.length > 0 && (
                            <div className="quick-stats">
                                <h3>Quick Stats</h3>
                                <div className="stats-row-home">
                                    <div className="stat-home">
                                        <strong>{sessions.length}</strong>
                                        <span>Sessions</span>
                                    </div>
                                    <div className="stat-home">
                                        <strong>{sessions.reduce((s, se) => s + se.reps, 0)}</strong>
                                        <span>Total Reps</span>
                                    </div>
                                    <div className="stat-home">
                                        <strong>
                                            {sessions.length > 0
                                                ? Math.round(sessions.reduce((s, se) => s + parseFloat(se.avgAccuracy), 0) / sessions.length)
                                                : 0}%
                                        </strong>
                                        <span>Accuracy</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {view === 'exercise' && selectedExercise && (
                    <Tracker
                        exercise={selectedExercise}
                        onComplete={handleExerciseComplete}
                        voiceEnabled={voiceEnabled}
                    />
                )}


            </main>

            <style jsx>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }

        .app {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .navbar {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(10px);
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .nav-brand {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.5rem;
          font-weight: 700;
          color: #667eea;
        }

        .nav-links {
          display: flex;
          gap: 0.5rem;
        }

        .nav-links button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border: none;
          background: transparent;
          color: #64748b;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
          font-weight: 500;
        }

        .nav-links button:hover, .nav-links button.active {
          background: #667eea;
          color: white;
        }

        .nav-links button.voice-active {
          background: #10b981;
          color: white;
        }

        .main-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
        }

        .hero {
          text-align: center;
          color: white;
          margin-bottom: 2rem;
        }

        .hero h1 {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .hero p {
          font-size: 1.1rem;
          opacity: 0.95;
        }

        .exercise-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .exercise-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .exercise-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        .exercise-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 1rem;
        }

        .exercise-card h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        .exercise-card p {
          color: #64748b;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .btn-card {
          background: transparent;
          border: 2px solid #667eea;
          color: #667eea;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
          width: 100%;
        }

        .btn-card:hover {
          background: #667eea;
          color: white;
        }

        .quick-stats {
          background: rgba(255, 255, 255, 0.95);
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .quick-stats h3 {
          color: #1e293b;
          margin-bottom: 1.5rem;
        }

        .stats-row-home {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .stat-home {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 12px;
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .stat-home strong {
          font-size: 2rem;
          color: #667eea;
        }

        .stat-home span {
          color: #64748b;
          font-size: 0.875rem;
        }

        .exercise-tracker {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 2rem;
        }

        .video-section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .video-container {
          position: relative;
          width: 100%;
          
          border-radius: 16px;
          overflow: hidden;
          background: #000;
        }

        .video-feed, .pose-canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .pose-canvas {
          pointer-events: none;
          z-index: 2;
          mix-blend-mode: screen;
          opacity: 0.95;
        }

        .video-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #94a3b8;
          text-align: center;
          padding: 2rem;
        }

        .video-placeholder svg {
          margin-bottom: 1rem;
          opacity: 0.6;
        }

        .video-placeholder small {
          opacity: 0.8;
          margin-top: 0.5rem;
        }

        .skeleton-legend {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(10px);
          padding: 0.75rem;
          border-radius: 8px;
          z-index: 3;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: white;
          font-size: 0.75rem;
        }

        .legend-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
        }

        .error-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(239, 68, 68, 0.95);
          color: white;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          text-align: center;
          z-index: 10;
        }

        .warning-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(245, 158, 11, 0.95);
          color: white;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          text-align: center;
          z-index: 10;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.95; }
          50% { opacity: 0.7; }
        }

        .feedback-overlay {
          position: absolute;
          top: 1rem;
          left: 1rem;
          right: 1rem;
          z-index: 5;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .feedback-main {
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(10px);
          color: white;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          font-size: 1.25rem;
          font-weight: 600;
          text-align: center;
          border-left: 4px solid #667eea;
          animation: slideIn 0.3s ease;
        }

        .feedback-main.phase-perfect {
          border-left-color: #10b981;
          background: rgba(16, 185, 129, 0.25);
        }

        .feedback-main.phase-almost {
          border-left-color: #f59e0b;
          background: rgba(245, 158, 11, 0.25);
        }

        .feedback-main.phase-too_low {
          border-left-color: #ef4444;
          background: rgba(239, 68, 68, 0.25);
        }

        .feedback-secondary {
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(10px);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 1rem;
          text-align: center;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .angle-indicator {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          z-index: 5;
        }

        .angle-circle {
          width: 80px;
          height: 80px;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(10px);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #667eea;
        }

        .angle-value {
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .progress-overlay {
          position: absolute;
          bottom: 1rem;
          left: 1rem;
          right: 100px;
          z-index: 5;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .progress-bar-container {
          flex: 1;
          height: 12px;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(10px);
          border-radius: 6px;
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #667eea 0%, #10b981 100%);
          border-radius: 6px;
          transition: width 0.3s ease;
        }

        .progress-text {
          color: white;
          font-weight: 700;
          font-size: 1rem;
          background: rgba(0, 0, 0, 0.85);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          min-width: 60px;
          text-align: center;
        }

        .live-stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .stat-box-live {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 12px;
          text-align: center;
        }

        .stat-label {
          color: #64748b;
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
        }

        .stat-value-live {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
        }

        .stat-value-live.phase-perfect {
          color: #10b981;
        }

        .stat-value-live.phase-almost {
          color: #f59e0b;
        }

        .stat-value-live.phase-too_low {
          color: #ef4444;
        }

        .exercise-info {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .exercise-info h2 {
          font-size: 2rem;
          color: #1e293b;
        }

        .description {
          color: #64748b;
          line-height: 1.6;
        }

        .instructions-box {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 12px;
        }

        .instructions-box h4 {
          margin-bottom: 1rem;
          color: #1e293b;
        }

        .instructions-box ol {
          padding-left: 1.5rem;
        }

        .instructions-box li {
          margin-bottom: 0.5rem;
          color: #64748b;
          line-height: 1.6;
        }

        .target-box {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 1.5rem;
          border-radius: 12px;
          color: white;
        }

        .target-box h4 {
          margin-bottom: 0.5rem;
        }

        .target-value {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .controls-box {
          margin-top: auto;
        }

        .btn-start {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.125rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.3s;
          width: 100%;
        }

        .btn-start:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }

        .btn-stop {
          background: #ef4444;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.125rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.3s;
          width: 100%;
        }

        .btn-stop:hover {
          background: #dc2626;
        }

        .dashboard {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .dashboard h2 {
          font-size: 2rem;
          margin-bottom: 2rem;
          color: #1e293b;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card-dash {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem;
          border-radius: 16px;
          color: white;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stat-big {
          font-size: 2.5rem;
          font-weight: 700;
        }

        .stat-small {
          font-size: 0.875rem;
          opacity: 0.9;
        }

        .chart-section {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 16px;
          margin-bottom: 2rem;
        }

        .chart-section h3 {
          margin-bottom: 1.5rem;
          color: #1e293b;
        }

        .recent-list {
          margin-top: 2rem;
        }

        .recent-list h3 {
          margin-bottom: 1.5rem;
          color: #1e293b;
        }

        .session-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: #f8fafc;
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 0.75rem;
          transition: all 0.3s;
        }

        .session-row:hover {
          background: #e2e8f0;
          transform: translateX(4px);
        }

        .session-info {
          flex: 1;
        }

        .session-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .session-date {
          font-size: 0.875rem;
          color: #64748b;
        }

        .session-badges {
          display: flex;
          gap: 0.5rem;
        }

        .badge {
          background: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #667eea;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #64748b;
        }

        .empty-state svg {
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .empty-state h3 {
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        @media (max-width: 1024px) {
          .exercise-tracker {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .navbar {
            padding: 1rem;
          }

          .nav-brand span {
            display: none;
          }

          .hero h1 {
            font-size: 2rem;
          }

          .exercise-grid {
            grid-template-columns: 1fr;
          }

          .stats-row-home {
            grid-template-columns: 1fr;
          }

          .live-stats-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </div>
    );
}